/**
 * @fileoverview Template collections — registry + pure HTML expansion engine.
 *
 * A "collection" is an iterable, recipient-scoped data source a template can
 * repeat over: the members of the recipient's corporate group, the security
 * interests held against the company by that group, the company's directors,
 * and so on. Collections give templates a way to handle unknown cardinality
 * (1 director or 9, 1 PPSR registration or 40) inside ONE document, instead
 * of fanning out one document per row or authoring N template variants.
 *
 * Four authoring primitives consume collections (all serialised as plain
 * HTML attributes so they survive the TipTap ⇄ DB round-trip):
 *
 *  1. Repeat block  — `<div data-type="repeat-block" data-collection="K">`
 *     Body is stamped once per item. Item fields inside use `{{item_<part>}}`
 *     placeholders (chip spans or raw). Also `<tr data-type="repeat-row">`
 *     for table-row repetition.
 *  2. Inline join   — `<span data-type="collection-join" data-collection="K"
 *     data-part="name">` → "A, B and C".
 *  3. Condition     — `<div data-type="condition-block" data-collection="K"
 *     data-min="2">` → body kept only when the count is in [min, max].
 *  4. Plural token  — `<span data-type="collection-plural" data-collection="K"
 *     data-one="director is" data-many="directors are">` (supports `{count}`).
 *
 * The engine is pure string→string so the same code runs in the day-one
 * renderer, exports, and unit tests. Collection DATA is resolved at
 * generation time (see collections.ts) and stored on the jobPrecedents row
 * as `collectionData: Record<collectionKey, Array<Record<part, string>>>`.
 *
 * Dependency-free by design (like contactFields.ts) — imported by both the
 * Convex backend and the frontend editor/renderer. (The signature-token
 * helper below is itself dependency-free, so that property holds.)
 */

import { parseSignatureImageToken } from "../../lib/signatureToken";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One resolved collection item: part → pre-formatted display string. */
export type CollectionItem = Record<string, string>;

/** All resolved collections for one generated document. */
export type CollectionData = Record<string, CollectionItem[]>;

export interface CollectionFieldMeta {
  /** Segment used in `{{item_<part>}}` placeholders. */
  part: string;
  label: string;
}

export interface CollectionMeta {
  key: string;
  label: string;
  /** Shown in the editor's insert menu. */
  description: string;
  fields: ReadonlyArray<CollectionFieldMeta>;
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

const CONTACT_ITEM_FIELDS: ReadonlyArray<CollectionFieldMeta> = [
  { part: "name", label: "Full Name" },
  { part: "first_name", label: "First Name" },
  { part: "last_name", label: "Last Name" },
  { part: "email", label: "Email" },
  { part: "phone", label: "Phone" },
  { part: "address", label: "Address" },
  { part: "abn", label: "ABN" },
  { part: "acn", label: "ACN" },
];

/**
 * Single source of truth for every collection a template can reference.
 * The resolver in collections.ts and the editor insert menu both key off
 * this registry — extend it here to expose a new collection everywhere.
 */
export const TEMPLATE_COLLECTIONS: ReadonlyArray<CollectionMeta> = [
  {
    key: "directors",
    label: "Directors",
    description: "Add all Directors when Director Contact is tagged.",
    fields: CONTACT_ITEM_FIELDS,
  },
  {
    key: "security_interests",
    label: "Security Interests",
    description:
      "Add details of multiple security interests for a secured creditor or use to create list of all secured parties & security interests.",
    fields: [
      { part: "ppsr_number", label: "PPSR Registration Number" },
      {
        part: "secured_party_identifier",
        label: "Giving of Notice Identifier",
      },
      { part: "registration_date", label: "Registration Date" },
      { part: "registration_end_date", label: "Registration End Date" },
      { part: "registration_status", label: "Registration Status" },
      { part: "collateral_class", label: "Collateral Class" },
      { part: "collateral_description", label: "Collateral Description" },
      { part: "pmsi", label: "PMSI (Yes/No)" },
      { part: "secured_party_name", label: "Secured Party Name" },
      { part: "owed_amount", label: "Owed Amount" },
      { part: "owed_amount_as_at", label: "Owed Amount As-At Date" },
    ],
  },
  {
    key: "secured_creditors",
    label: "Secured Creditors",
    description:
      "Add all Secured Creditors on this job when Secured Creditor Contact is tagged.",
    fields: CONTACT_ITEM_FIELDS,
  },
  {
    key: "shareholders",
    label: "Shareholders",
    description: "Add all Shareholders when Shareholder Contact is tagged.",
    fields: CONTACT_ITEM_FIELDS,
  },
  {
    key: "employees",
    label: "Employees",
    description: "Add all Employees when Employee Contact is tagged.",
    fields: CONTACT_ITEM_FIELDS,
  },
  {
    key: "group_members",
    label: "Grouped Secured Creditors",
    description:
      "List out all entities in a group of Secured Creditors when group structure exists.",
    fields: CONTACT_ITEM_FIELDS,
  },
  {
    key: "signatories",
    label: "Signatories",
    description: "Add multiple firm signatories.",
    fields: [
      { part: "name", label: "Full Name" },
      { part: "email", label: "Email" },
      { part: "signature", label: "Signature" },
    ],
  },
  {
    key: "firm_professionals",
    label: "Firm Contacts — Professionals",
    description:
      "Add all directory contacts in Professional category when tagged.",
    fields: [
      { part: "name", label: "Full Name" },
      { part: "email", label: "Email" },
      { part: "phone", label: "Phone" },
    ],
  },
];

export const TEMPLATE_COLLECTIONS_BY_KEY: ReadonlyMap<string, CollectionMeta> =
  new Map(TEMPLATE_COLLECTIONS.map((c) => [c.key, c]));

// ---------------------------------------------------------------------------
// Markup scanning
// ---------------------------------------------------------------------------

const COLLECTION_ATTR_RE = /data-collection="([a-z0-9_]+)"/gi;

/** Every collection key referenced anywhere in a template body. */
export function collectCollectionKeys(html: string): Set<string> {
  const keys = new Set<string>();
  for (const match of html.matchAll(COLLECTION_ATTR_RE)) {
    keys.add(match[1].toLowerCase());
  }
  return keys;
}

/** Cheap test used to skip the whole pipeline for scalar-only templates. */
export function hasCollectionMarkup(html: string): boolean {
  return html.includes("data-collection=");
}

// ---------------------------------------------------------------------------
// Expansion engine
// ---------------------------------------------------------------------------

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Find the index just past the matching close tag for the block opening at
 * `openStart` (index of "<"). Depth-counts same-tag opens/closes, so block
 * bodies may contain nested tags of the same name (signature divs, layout
 * divs). Returns -1 when unbalanced.
 */
function findMatchingClose(
  html: string,
  openStart: number,
  tagName: string,
): number {
  const openRe = new RegExp(`<${tagName}\\b`, "gi");
  const closeRe = new RegExp(`</${tagName}\\s*>`, "gi");
  let depth = 0;
  let cursor = openStart;
  for (;;) {
    openRe.lastIndex = cursor;
    closeRe.lastIndex = cursor;
    const open = openRe.exec(html);
    const close = closeRe.exec(html);
    if (!close) return -1;
    if (open && open.index < close.index) {
      depth += 1;
      cursor = open.index + open[0].length;
    } else {
      depth -= 1;
      cursor = close.index + close[0].length;
      if (depth === 0) return cursor;
    }
  }
}

interface BlockMatch {
  /** Index of the block's "<". */
  start: number;
  /** Index just past the closing tag. */
  end: number;
  /** The opening tag's full text. */
  openTag: string;
  /** Inner HTML between open and close tags. */
  inner: string;
  collection: string;
}

/** Locate the first block of `tagName` carrying `data-type="dataType"`. */
function findBlock(
  html: string,
  tagName: string,
  dataType: string,
  fromIndex: number,
): BlockMatch | null {
  const openTagRe = new RegExp(
    `<${tagName}\\b[^>]*data-type="${dataType}"[^>]*>`,
    "gi",
  );
  openTagRe.lastIndex = fromIndex;
  const open = openTagRe.exec(html);
  if (!open) return null;
  const end = findMatchingClose(html, open.index, tagName);
  if (end === -1) return null;
  const innerStart = open.index + open[0].length;
  const closeTagLength = end - html.slice(0, end).lastIndexOf(`</${tagName}`);
  const inner = html.slice(innerStart, end - closeTagLength);
  const collectionMatch = /data-collection="([a-z0-9_]+)"/i.exec(open[0]);
  return {
    start: open.index,
    end,
    openTag: open[0],
    inner,
    collection: (collectionMatch?.[1] ?? "").toLowerCase(),
  };
}

/**
 * Render one item value. Signature values use the same conventions as the
 * scalar `{{signature}}` field: an `__sig_image__:<url>` token renders the
 * stored signature image, and a plain-text signature renders in the
 * handwriting font. Everything else is escaped text.
 */
function renderItemValue(part: string, value: string): string {
  if (value.startsWith("__sig_image__:")) {
    const { url, scale } = parseSignatureImageToken(value);
    return `<img src="${escapeHtml(url)}" alt="Signature" style="max-height:${Math.round(60 * scale)}px;max-width:${Math.round(240 * scale)}px;object-fit:contain;margin:4px 0;" />`;
  }
  if (part === "signature" && value) {
    return `<span style="font-family:'Caveat',cursive;font-size:22px;line-height:1.4;">${escapeHtml(value)}</span>`;
  }
  return escapeHtml(value);
}

/**
 * Substitute `{{item_<part>}}` placeholders (raw or chip-span form) inside
 * one repeated fragment with the item's values. Unknown parts resolve to "".
 */
function fillItemFields(fragment: string, item: CollectionItem): string {
  // Chip spans: <span data-type="field-placeholder" data-field-key="item_x">…</span>
  // then raw {{item_x}} tokens.
  return fragment
    .replace(
      /<span[^>]*data-field-key="item_([a-z0-9_]+)"[^>]*>[^<]*<\/span>/gi,
      (_m, part: string) => renderItemValue(part, item[part] ?? ""),
    )
    .replace(/\{\{item_([a-z0-9_]+)\}\}/gi, (_m, part: string) =>
      renderItemValue(part, item[part] ?? ""),
    );
}

/** Add 1-based `index` / `ordinal` parts to each item at expansion time. */
function withPositionParts(item: CollectionItem, i: number): CollectionItem {
  return { ...item, index: String(i + 1), ordinal: ordinal(i + 1) };
}

function ordinal(n: number): string {
  const rem10 = n % 10;
  const rem100 = n % 100;
  if (rem10 === 1 && rem100 !== 11) return `${n}st`;
  if (rem10 === 2 && rem100 !== 12) return `${n}nd`;
  if (rem10 === 3 && rem100 !== 13) return `${n}rd`;
  return `${n}th`;
}

/**
 * Join a list of strings grammatically: "A", "A and B", "A, B and C".
 * Pass `finalWord: "none"` (or "") for a plain comma list: "A, B, C".
 */
export function joinList(values: string[], finalWord = "and"): string {
  const parts = values.filter((v) => v.trim().length > 0);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  if (finalWord === "none" || finalWord.trim() === "") {
    return parts.join(", ");
  }
  return `${parts.slice(0, -1).join(", ")} ${finalWord} ${parts[parts.length - 1]}`;
}

const MAX_EXPANSION_PASSES = 20;

/**
 * Expand every collection primitive in `html` against `data`.
 *
 * Missing collections behave as empty lists: repeats render nothing, joins
 * render "", conditions with `min >= 1` drop their body. Scalar
 * `{{field}}` placeholders are untouched — run this BEFORE the scalar
 * substitution pass so repeated fragments' non-item chips still resolve.
 */
export function expandCollectionMarkup(
  html: string,
  data: CollectionData,
): string {
  let out = html;

  const items = (key: string): CollectionItem[] => data[key] ?? [];

  // Pass order matters: conditions may WRAP repeats, so unwrap/drop them
  // first; then block repeats (which may contain rows); then row repeats;
  // then inline join/plural tokens. Loop so a condition unwrapping a
  // repeat gets that repeat expanded on the next pass.
  for (let pass = 0; pass < MAX_EXPANSION_PASSES; pass += 1) {
    let changed = false;

    // 1. Condition blocks.
    for (;;) {
      const block = findBlock(out, "div", "condition-block", 0);
      if (!block) break;
      const count = items(block.collection).length;
      const min = Number(/data-min="(\d+)"/i.exec(block.openTag)?.[1] ?? "0");
      const maxRaw = /data-max="(\d+)"/i.exec(block.openTag)?.[1];
      const max = maxRaw === undefined ? Infinity : Number(maxRaw);
      const keep = count >= min && count <= max;
      out =
        out.slice(0, block.start) +
        (keep ? block.inner : "") +
        out.slice(block.end);
      changed = true;
    }

    // 2. Block repeats. Default mode stacks the stamped fragments
    //    vertically; `data-mode="columns"` lays them side by side in one
    //    table row (equal widths) — built for multi-signer signature
    //    blocks, and table-based so Word's altChunk renders it reliably.
    for (;;) {
      const block = findBlock(out, "div", "repeat-block", 0);
      if (!block) break;
      const stamped = items(block.collection).map((item, i) =>
        fillItemFields(block.inner, withPositionParts(item, i)),
      );
      const isColumns = /data-mode="columns"/i.test(block.openTag);
      const expanded =
        isColumns && stamped.length > 0
          ? `<table style="width:100%;border-collapse:collapse;"><tbody><tr>` +
            stamped
              .map(
                (fragment) =>
                  `<td style="width:${(100 / stamped.length).toFixed(2)}%;vertical-align:bottom;padding:0 24px 0 0;">${fragment}</td>`,
              )
              .join("") +
            `</tr></tbody></table>`
          : stamped.join("");
      out = out.slice(0, block.start) + expanded + out.slice(block.end);
      changed = true;
    }

    // 3. List-item repeats (<li data-type="repeat-item"> inside a
    //    <ul>/<ol>) — one bullet / number per item. Depth-scanned since
    //    nested lists nest <li>.
    for (;;) {
      const block = findBlock(out, "li", "repeat-item", 0);
      if (!block) break;
      const attrs = block.openTag
        .replace(/^<li/i, "")
        .replace(/>$/, "")
        .replace(/\s*data-type="repeat-item"/gi, "")
        .replace(/\s*data-collection="[a-z0-9_]+"/gi, "")
        .trim();
      const expanded = items(block.collection)
        .map(
          (item, i) =>
            `<li${attrs ? ` ${attrs}` : ""}>${fillItemFields(block.inner, withPositionParts(item, i))}</li>`,
        )
        .join("");
      out = out.slice(0, block.start) + expanded + out.slice(block.end);
      changed = true;
    }

    // 4. Table-row repeats (<tr> never nests — lazy match is safe).
    out = out.replace(
      /<tr\b([^>]*)data-type="repeat-row"([^>]*)>([\s\S]*?)<\/tr>/gi,
      (_m, pre: string, post: string, inner: string) => {
        changed = true;
        const collection =
          /data-collection="([a-z0-9_]+)"/i
            .exec(pre + post)?.[1]
            ?.toLowerCase() ?? "";
        const attrs = (pre + post)
          .replace(/data-type="repeat-row"/gi, "")
          .replace(/data-collection="[a-z0-9_]+"/gi, "")
          .trim();
        return items(collection)
          .map(
            (item, i) =>
              `<tr${attrs ? ` ${attrs}` : ""}>${fillItemFields(inner, withPositionParts(item, i))}</tr>`,
          )
          .join("");
      },
    );

    // 5. Inline joins.
    out = out.replace(
      /<span\b[^>]*data-type="collection-join"[^>]*>[^<]*<\/span>/gi,
      (tag) => {
        changed = true;
        const collection =
          /data-collection="([a-z0-9_]+)"/i.exec(tag)?.[1]?.toLowerCase() ?? "";
        // `data-part` may concatenate fields with "+" (e.g.
        // "first_name+last_name"), joined per item by data-part-sep
        // (default: a space) — so "John Smith, Jane Doe and Wei Chen".
        const partSpec = /data-part="([a-z0-9_+]+)"/i.exec(tag)?.[1] ?? "name";
        const partSep = /data-part-sep="([^"]*)"/i.exec(tag)?.[1] ?? " ";
        const parts = partSpec.split("+").filter(Boolean);
        const finalWord = /data-final="([^"]+)"/i.exec(tag)?.[1] ?? "and";
        return escapeHtml(
          joinList(
            items(collection).map((item) =>
              parts
                .map((p) => item[p] ?? "")
                .filter((v) => v.trim().length > 0)
                .join(partSep),
            ),
            finalWord,
          ),
        );
      },
    );

    // 6. Plural tokens.
    out = out.replace(
      /<span\b[^>]*data-type="collection-plural"[^>]*>[^<]*<\/span>/gi,
      (tag) => {
        changed = true;
        const collection =
          /data-collection="([a-z0-9_]+)"/i.exec(tag)?.[1]?.toLowerCase() ?? "";
        const count = items(collection).length;
        const one = /data-one="([^"]*)"/i.exec(tag)?.[1] ?? "";
        const many = /data-many="([^"]*)"/i.exec(tag)?.[1] ?? "";
        const decode = (s: string) =>
          s
            .replace(/&quot;/g, '"')
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&amp;/g, "&");
        const text = (count === 1 ? decode(one) : decode(many)).replace(
          /\{count\}/g,
          String(count),
        );
        return escapeHtml(text);
      },
    );

    if (!changed) break;
  }

  return out;
}
